import { FC, useEffect, useState, useCallback } from "react";
import {
  StyledBondDelivery,
  StyledBondDeliveryHeader,
  StyledSpinnerContainer,
  StyledBondDeliveryField,
  StyledBondDeliveryLabel,
  StyledBondDeliveryText,
} from "./BondDelivery.style";
import { Spinner } from "../../../Spinner/Spinner";
import Radio from "../../../Radio/Radio";
import { FormStart } from "../../FormStart";
import { getShippingSchema } from "./schema";
import { BondRecordData, ShippingOption } from "../../FormBondApp.types";

export interface IShippingMethod {
  created_at: string | null;
  id: string;
  name: string;
  price: number;
  updated_at: string | null;
}

export type IShippingSubmit = Pick<IShippingMethod, "id" | "price">;

interface BondDeliveryProps {
  bondRecord?: BondRecordData;
  isLoading: boolean;
  shippingMethods: any;
  onBlur?: () => {};
  onSubmit: any;
  isBusiness?: boolean;
  forms: any;
  widgets: any;
  error: any;
  shippingOptions?: ShippingOption[];
  fetchShippingOptions?: () => Promise<void>;
}

const BondDelivery: FC<BondDeliveryProps> = (props): JSX.Element => {
  const {
    bondRecord,
    isLoading,
    shippingMethods,
    onBlur = () => ({}),
    onSubmit,
    isBusiness,
    forms,
    widgets,
    shippingOptions,
    fetchShippingOptions,
  } = props;

  const currentShippingData =
    shippingOptions && shippingOptions.length > 0 
      ? shippingOptions[shippingOptions.length - 1] 
      : null;

  const data = (isBusiness ? forms?.[2]?.data : forms?.[1]?.data) ?? {};
  const { first_name, middle_name, last_name, suffix } = data;

  // Prepare default data from applicant info
  const defaultData = {
    ...data,
    recipient_name: `${first_name} ${middle_name || ''} ${last_name} ${suffix || ''}`.trim(),
    zip: data.mailing_zip,
    street: data.mailing_street,
    city: data.mailing_city,
    state_code: data.mailing_state,
  };

  const [selectedShippingMethod, setSelectedShippingMethod] = useState(
    currentShippingData?.shipping_method_id || "USPS"
  );
  const [shippingData, setShippingData] = useState(currentShippingData || defaultData);
  const [shippingMethodsData, setShippingMethodsData] = useState(shippingMethods);
  const [isLoadingShippingData, setIsLoadingShippingData] = useState(false);

  const bondRecordData = bondRecord;

  const options = shippingMethodsData.map((method: any) => ({
    value: method.id,
    label: `${
        method.price
            ? `$${method.price} ${method.name}`
            : `FREE shipping via ${method.id}`
    }`,
  }));

  const handleUpdateShippingMethods = (shippingMethodsData: any) => {
    setShippingMethodsData(shippingMethodsData.methods);
  }

  const handleSubmit = useCallback(async () => {
    const shippingMethod = shippingMethodsData.find(
      (method: IShippingMethod) => method.id === selectedShippingMethod
    );

    const { zip, street, city, state_code, recipient_name, id } = shippingData;

    const shippingId = id;

    if (shippingMethod && onSubmit) {
      const { id, price } = shippingMethod;
      onSubmit({
        id: shippingId,
        shipping_method_id: id,
        price: Number(price),
        zip,
        street,
        city,
        state_code,
        recipient_name,
      });
    }
  }, [shippingMethodsData, selectedShippingMethod, shippingData, onSubmit]);

  useEffect(() => {
    if (!bondRecord?.is_shipping_required) {
      handleSubmit();
    }
  }, [bondRecord?.is_shipping_required, handleSubmit]);

  // Only fetch shipping options if we don't have any yet
  useEffect(() => {
    const loadShippingData = async () => {
      // Only fetch if we have a bond record, fetch function is available,
      // shipping is required, and we don't already have shipping options
      if (bondRecord?.is_shipping_required && 
          fetchShippingOptions && 
          (!shippingOptions || shippingOptions.length === 0)) {
        setIsLoadingShippingData(true);
        try {
          await fetchShippingOptions();
        } catch (error) {
          console.error("Error fetching shipping options:", error);
        } finally {
          setIsLoadingShippingData(false);
        }
      }
    };

    loadShippingData();
  }, [bondRecord, shippingOptions, fetchShippingOptions]);

  // Update shipping data when shipping options change
  useEffect(() => {
    if (currentShippingData) {
      setShippingData(currentShippingData);
      setSelectedShippingMethod(currentShippingData.shipping_method_id);
    }
  }, [shippingOptions, currentShippingData]);

  const schema = getShippingSchema();

  const uiSchema = {
    "ui:submitButtonOptions": {
      props: {
        className: "submit_button",
      },
      submitText: "Continue to Payment",
    },
  };

  return (
    <>
      {isLoading || isLoadingShippingData || !bondRecord?.is_shipping_required ? (
        <StyledSpinnerContainer>
          <Spinner />
          <span>Loading...</span>
        </StyledSpinnerContainer>
      ) : (
        <StyledBondDelivery>
          <StyledBondDeliveryHeader>
            <StyledBondDeliveryLabel>{`The ${
              bondRecordData?.obligee_name ||
              bondRecordData?.product_obligee_name
            } ${bondRecordData?.product_base_name || ""} Bond will cost $${
              bondRecordData?.bond_premium_amount || ""
            } plus shipping costs.`}</StyledBondDeliveryLabel>
          </StyledBondDeliveryHeader>
          <StyledBondDeliveryField>
            <StyledBondDeliveryLabel>
              Please select shipping option
            </StyledBondDeliveryLabel>
            {options.map((item: any, index: number) => {
              return (
                <Radio
                  key={index}
                  onChange={(value: any) => setSelectedShippingMethod(value)}
                  name="shipping"
                  tabIndex={index}
                  value={item.value}
                  label={item.label}
                  onBlur={onBlur}
                  isChecked={item.value === selectedShippingMethod}
                />
              );
            })}
          </StyledBondDeliveryField>
          <StyledBondDeliveryText>
            Please verify or update the shipping information below:
          </StyledBondDeliveryText>
          <FormStart
            isLoading={false}
            data={shippingData}
            setData={(data: any) => {
              setShippingData(data);
            }}
            submit={handleSubmit}
            schema={schema}
            widgets={widgets}
            isSkipBond={false}
            uiSchema={uiSchema}
            handleUpdateShippingMethods={handleUpdateShippingMethods}
          />
        </StyledBondDelivery>
      )}
    </>
  );
};

export default BondDelivery;
